<template>
  <div class="container-fluid blabla-trip-flow">
    <div class="page-title">
      <div class="row">
        <div class="col-6"><h3>Crear viaje</h3></div>
      </div>
    </div>

    <div v-if="!canPublish" class="alert alert-warning">
      Solo los usuarios con rol <strong>Viajero</strong> o <strong>Administrador</strong> pueden publicar viajes.
    </div>

    <div class="row mt-3 justify-content-center">
      <div class="col-12 col-xxl-10">
        <div class="card flow-card">
          <div class="card-header pb-0">
            <h4 class="mb-0">
              Paso {{ step }} de 4
              <span v-if="isReturnFlow" class="badge bg-info ms-2">Viaje de regreso</span>
            </h4>
            <span class="text-muted small">{{ stepLabel }}</span>
          </div>
          <div class="card-body">
            <h2 class="flow-title">{{ stepQuestionText }}</h2>

            <div v-if="step === 1">
              <MapLocationPicker
                label="Direccion de salida del viaje"
                subtitle="Mueve el mapa para fijar la direccion"
                :initialLatitude="departure.latitude"
                :initialLongitude="departure.longitude"
                :initialAddress="departure.address"
                @confirm="onConfirmLocation('departure', $event)"
              />
            </div>

            <div v-else-if="step === 2">
              <MapLocationPicker
                label="Punto de destino del viaje"
                subtitle="Mueve el mapa para fijar el destino"
                :initialLatitude="destinationPoint.latitude"
                :initialLongitude="destinationPoint.longitude"
                :initialAddress="destinationPoint.address"
                @confirm="onConfirmLocation('destinationPoint', $event)"
              />
            </div>

            <div v-else-if="step === 3" class="flow-section">
              <div class="mb-3">
                <h5>Rutas sugeridas</h5>
                
              </div>
              <div v-if="loadingRoutes" class="text-muted">Calculando rutas...</div>
              <div v-else-if="routeError" class="alert alert-warning py-2">{{ routeError }}</div>
              <div v-else-if="routeOptions.length === 0" class="text-muted">Aun no hay rutas cargadas.</div>
              <template v-else>
                <div ref="routeMapContainer" class="route-map mb-3"></div>
                <div class="list-group route-list">
                  <button
                    v-for="(route, idx) in routeOptions"
                    :key="idx"
                    type="button"
                    class="list-group-item list-group-item-action"
                    :class="{ active: selectedRouteIndex === idx }"
                    @click="selectRoute(idx)"
                  >
                    <div class="fw-semibold">Ruta {{ idx + 1 }} - {{ formatDuration(route.duration) }} - {{ formatDistance(route.distance) }}</div>
                    <div class="small">{{ route.distanceText }} | {{ route.durationText }}</div>
                  </button>
                </div>
              </template>
            </div>

            <div v-else-if="step === 4" class="flow-section">
             
              <div class="date-time-stack">
                <div class="date-block">
                  
                  <calenderGeneral @date-selected="onDateSelected" />
                
                </div>
                <div class="time-block">
                  <label class="form-label">Hora del viaje</label>
                  <select v-model="form.horaViaje" class="form-select bbc-input">
                    <option value="">Selecciona una hora</option>
                    <option v-for="option in availableTimeOptions" :key="option" :value="option">{{ option }}</option>
                  </select>
                </div>
              </div>
              <div class="delivery-flex mt-3">
                <div class="form-check">
                  <input id="deliveryFlexible" v-model="form.deliveryFlexible" class="form-check-input" type="checkbox" />
                  <label class="form-check-label" for="deliveryFlexible">
                    Puedo entregar paquetes en otro sitio acordado (no solo en el punto de destino).
                  </label>
                </div>
                <div class="small text-muted mt-1">
                  Si activas esta opción, los clientes verán que aceptas entrega personalizada.
                </div>
              </div>
              <div v-if="dateTimeValidationMessage" class="alert alert-warning py-2 mt-3 mb-0">
                {{ dateTimeValidationMessage }}
              </div>
              <div class="col-12 d-flex justify-content-between mt-3">
                <button class="btn btn-outline-secondary" :disabled="loading" @click="prevStep">Atrás</button>
                <button class="btn btn-primary" :disabled="loading || !canPublish || !isDateTimeValid" @click="submit">
                  {{ loading ? 'Publicando...' : 'Publicar viaje' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-3 d-flex justify-content-between flow-actions">
          <button class="btn btn-outline-secondary" :disabled="step === 1 || loading" @click="prevStep">Anterior</button>
          <button v-if="showContinueButton" class="btn btn-success" :disabled="loading || !canContinueStep" @click="nextStep">
            Continuar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { tripsApi } from '@/api/trips';
import MapLocationPicker from '@/components/trips/MapLocationPicker.vue';
import calenderGeneral from '@/components/widgets/general/calenderGeneral.vue';
import { normalizeApiError } from '@/utils/error-handler';

export default {
  name: 'CrearViaje',
  components: { MapLocationPicker, calenderGeneral },
  data() {
    return {
      step: 1,
      loading: false,
      loadingRoutes: false,
      isReturnFlow: false,
      departure: { latitude: null, longitude: null, address: '' },
      pickupPoint: { latitude: null, longitude: null, address: '' },
      destinationPoint: { latitude: null, longitude: null, address: '' },
      routeOptions: [],
      selectedRouteIndex: null,
      routeError: '',
      routeMap: null,
      lastAutoRouteKey: '',
      form: { fechaViaje: '', horaViaje: '', deliveryFlexible: false },
      timeOptions: [],
    };
  },
  computed: {
    canPublish() {
      const role = this.$store.getters['auth/role'];
      return role === 'CONDUCTOR_VIAJERO' || role === 'ADMINISTRADOR';
    },
    stepLabel() {
      return {
        1: 'Direccion de salida y recogida',
        2: 'Punto de destino y entrega',
        3: 'Seleccion de ruta',
        4: 'Fecha y hora de salida',
      }[this.step] || '';
    },
    stepQuestionText() {
      return {
        1: 'Desde donde sales?',
        2: 'A donde viajas y entregaras los paquetes?',
        3: 'Que ruta tomaras?',
        4: 'Que dia y a que hora sales?',
      }[this.step] || 'Publicar viaje';
    },
    canCalculateRoutes() {
      return this.hasLocation(this.pickupPoint) && this.hasLocation(this.destinationPoint);
    },
    showContinueButton() {
      return this.step >= 1 && this.step <= 3;
    },
    canContinueStep() {
      if (this.step === 1) return this.hasLocation(this.departure);
      if (this.step === 2) return this.hasLocation(this.destinationPoint);
      if (this.step === 3) return this.selectedRouteIndex !== null;
      return false;
    },
    availableTimeOptions() {
      if (!this.form.fechaViaje) return this.timeOptions;
      const today = this.getTodayISODate();
      if (this.form.fechaViaje !== today) return this.timeOptions;
      const now = new Date();
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      return this.timeOptions.filter((time) => {
        const [h, m] = time.split(':').map(Number);
        return h * 60 + m >= nowMinutes;
      });
    },
    isDateTimeValid() {
      if (!this.form.fechaViaje || !this.form.horaViaje) return false;
      const selectedDateTime = new Date(`${this.form.fechaViaje}T${this.form.horaViaje}:00`);
      if (Number.isNaN(selectedDateTime.getTime())) return false;
      return selectedDateTime.getTime() >= Date.now();
    },
    dateTimeValidationMessage() {
      if (!this.form.fechaViaje || !this.form.horaViaje) return '';
      return this.isDateTimeValid ? '' : 'La fecha y hora seleccionadas no pueden ser anteriores al momento actual.';
    },
  },
  mounted() {
    this.timeOptions = this.buildTimeOptions();
  },
  watch: {
    step(newStep) {
      if (newStep === 3) {
        this.autoLoadRoutesIfNeeded();
      }
    },
  },
  beforeUnmount() {
    if (this.routeMap) {
      this.routeMap.remove();
      this.routeMap = null;
    }
  },
  methods: {
    onDateSelected(dateValue) {
      this.form.fechaViaje = dateValue;
      if (this.form.horaViaje && !this.availableTimeOptions.includes(this.form.horaViaje)) {
        this.form.horaViaje = '';
      }
    },
    buildTimeOptions() {
      const result = [];
      for (let h = 0; h < 24; h += 1) {
        for (let m = 0; m < 60; m += 15) {
          result.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
        }
      }
      return result;
    },
    getTodayISODate() {
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },
    hasLocation(loc) {
      return loc && Number.isFinite(loc.latitude) && Number.isFinite(loc.longitude) && !!(loc.address || '').trim();
    },
    onConfirmLocation(key, loc) {
      this[key] = { ...this[key], ...loc, address: loc.address || this[key].address };
      if (key === 'departure') {
        // La salida también es punto de recogida de paquetes.
        this.pickupPoint = { ...this.departure };
      }
      if (this.step < 4) this.step += 1;
      this.$nextTick(() => this.autoLoadRoutesIfNeeded());
    },
    nextStep() {
      if (!this.canContinueStep) return;
      if (this.step < 4) this.step += 1;
      this.$nextTick(() => this.autoLoadRoutesIfNeeded());
    },
    prevStep() {
      if (this.step > 1) this.step -= 1;
    },
    getMapboxToken() {
      return process.env.VUE_APP_MAPBOX_TOKEN || '';
    },
    getRouteKey() {
      if (!this.hasLocation(this.pickupPoint) || !this.hasLocation(this.destinationPoint)) return '';
      return [
        this.pickupPoint.latitude,
        this.pickupPoint.longitude,
        this.destinationPoint.latitude,
        this.destinationPoint.longitude,
      ].join('|');
    },
    autoLoadRoutesIfNeeded() {
      if (this.step !== 3 || this.loadingRoutes || !this.canCalculateRoutes) return;
      const routeKey = this.getRouteKey();
      if (!routeKey) return;
      if (routeKey === this.lastAutoRouteKey && this.routeOptions.length) return;
      this.lastAutoRouteKey = routeKey;
      this.loadRoutes();
    },
    async loadRoutes() {
      this.routeError = '';
      this.loadingRoutes = true;
      this.selectedRouteIndex = null;
      try {
        const token = this.getMapboxToken();
        if (!token) throw new Error('Falta VUE_APP_MAPBOX_TOKEN para calcular rutas.');
        const from = `${this.pickupPoint.longitude},${this.pickupPoint.latitude}`;
        const to = `${this.destinationPoint.longitude},${this.destinationPoint.latitude}`;
        const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${from};${to}?alternatives=true&geometries=geojson&overview=full&language=es&access_token=${token}`;
        const resp = await fetch(url);
        const data = await resp.json();
        const routes = data?.routes || [];
        if (!routes.length) throw new Error('No se encontraron rutas sugeridas.');
        this.routeOptions = routes.map((r) => ({
          distance: r.distance,
          duration: r.duration,
          geometry: r.geometry,
          distanceText: this.formatDistance(r.distance),
          durationText: this.formatDuration(r.duration),
        }));
        this.selectedRouteIndex = 0;
        this.$nextTick(() => this.renderRouteMap());
      } catch (e) {
        this.routeOptions = [];
        this.routeError = normalizeApiError(e, 'No fue posible calcular rutas.');
      } finally {
        this.loadingRoutes = false;
      }
    },
    renderRouteMap() {
      const mapboxgl = window.mapboxgl;
      if (!mapboxgl || !this.$refs.routeMapContainer) return;
      const token = this.getMapboxToken();
      if (!token) return;
      mapboxgl.accessToken = token;
      if (this.routeMap) this.routeMap.remove();
      this.routeMap = new mapboxgl.Map({
        container: this.$refs.routeMapContainer,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [this.pickupPoint.longitude, this.pickupPoint.latitude],
        zoom: 7,
      });
      this.routeMap.on('load', () => {
        this.routeOptions.forEach((route, idx) => {
          const id = `route-${idx}`;
          this.routeMap.addSource(id, {
            type: 'geojson',
            data: { type: 'Feature', geometry: route.geometry },
          });
          this.routeMap.addLayer({
            id,
            type: 'line',
            source: id,
            layout: { 'line-join': 'round', 'line-cap': 'round' },
            paint: {
              'line-color': idx === this.selectedRouteIndex ? '#0d6efd' : '#7aa7ff',
              'line-width': idx === this.selectedRouteIndex ? 6 : 4,
              'line-opacity': idx === this.selectedRouteIndex ? 1 : 0.55,
            },
          });
        });
        this.fitMapToRoute(this.selectedRouteIndex ?? 0);
      });
    },
    selectRoute(index) {
      this.selectedRouteIndex = index;
      if (!this.routeMap) return;
      this.routeOptions.forEach((_, idx) => {
        const id = `route-${idx}`;
        if (!this.routeMap.getLayer(id)) return;
        this.routeMap.setPaintProperty(id, 'line-color', idx === index ? '#0d6efd' : '#7aa7ff');
        this.routeMap.setPaintProperty(id, 'line-width', idx === index ? 6 : 4);
        this.routeMap.setPaintProperty(id, 'line-opacity', idx === index ? 1 : 0.55);
      });
      this.fitMapToRoute(index);
    },
    fitMapToRoute(index) {
      if (!this.routeMap || !this.routeOptions[index]?.geometry?.coordinates?.length) return;
      const coords = this.routeOptions[index].geometry.coordinates;
      let minLng = coords[0][0];
      let maxLng = coords[0][0];
      let minLat = coords[0][1];
      let maxLat = coords[0][1];
      coords.forEach((c) => {
        if (c[0] < minLng) minLng = c[0];
        if (c[0] > maxLng) maxLng = c[0];
        if (c[1] < minLat) minLat = c[1];
        if (c[1] > maxLat) maxLat = c[1];
      });
      this.routeMap.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 50, duration: 500 },
      );
    },
    formatDuration(seconds) {
      if (!Number.isFinite(seconds)) return '-';
      const totalMinutes = Math.round(seconds / 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return hours ? `${hours} h ${minutes} min` : `${minutes} min`;
    },
    formatDistance(meters) {
      if (!Number.isFinite(meters)) return '-';
      return `${(meters / 1000).toFixed(1)} km`;
    },
    getPayloadForCurrentTrip() {
      const fechaViajeISO = new Date(`${this.form.fechaViaje}T00:00:00`).toISOString();
      const horaViajeISO = new Date(`${this.form.fechaViaje}T${this.form.horaViaje}`).toISOString();
      return {
        fechaViaje: fechaViajeISO,
        horaViaje: horaViajeISO,
        deliveryFlexible: Boolean(this.form.deliveryFlexible),
        origen: {
          latitude: this.pickupPoint.latitude,
          longitude: this.pickupPoint.longitude,
          direccion: this.pickupPoint.address,
        },
        destino: {
          latitude: this.destinationPoint.latitude,
          longitude: this.destinationPoint.longitude,
          direccion: this.destinationPoint.address,
        },
      };
    },
    resetWizardForNewTrip() {
      this.step = 1;
      this.loadingRoutes = false;
      this.routeOptions = [];
      this.selectedRouteIndex = null;
      this.routeError = '';
      this.lastAutoRouteKey = '';
      this.form = { fechaViaje: '', horaViaje: '', deliveryFlexible: false };
      this.departure = { latitude: null, longitude: null, address: '' };
      this.pickupPoint = { latitude: null, longitude: null, address: '' };
      this.destinationPoint = { latitude: null, longitude: null, address: '' };
    },
    prepareReturnTrip() {
      const previous = {
        departure: { ...this.departure },
        pickupPoint: { ...this.pickupPoint },
        destinationPoint: { ...this.destinationPoint },
      };
      this.resetWizardForNewTrip();
      this.isReturnFlow = true;
      this.departure = { ...previous.destinationPoint };
      this.pickupPoint = { ...previous.destinationPoint };
      this.destinationPoint = { ...previous.pickupPoint };
    },
    async suggestProfilePhotoIfNeeded() {
      const role = this.$store.getters['auth/role'];
      const user = this.$store.getters['auth/user'];
      if (role === 'CONDUCTOR_VIAJERO' && !user?.avatarUrl) {
        await Swal.fire({
          icon: 'info',
          title: 'Sugerencia',
          text: 'Te recomendamos agregar una foto de perfil para generar mayor confianza en tus envios.',
          confirmButtonColor: '#0d6efd',
        });
      }
    },
    async submit() {
      this.loading = true;
      try {
        if (!this.canPublish) throw new Error('No tienes permisos para publicar viajes.');
        if (!this.form.fechaViaje || !this.form.horaViaje) throw new Error('Selecciona fecha y hora.');
        if (!this.isDateTimeValid) throw new Error('Selecciona una fecha y hora válidas (no anteriores al momento actual).');
        if (this.selectedRouteIndex === null) throw new Error('Selecciona una ruta.');

        await tripsApi.create(this.getPayloadForCurrentTrip());
        await Swal.fire({
          icon: 'success',
          title: this.isReturnFlow ? 'Viaje de regreso publicado' : 'Viaje publicado',
          text: 'Tu viaje ya está disponible para consulta.',
          confirmButtonColor: '#0d6efd',
        });

        if (!this.isReturnFlow) {
          const confirmRoundTrip = await Swal.fire({
            icon: 'question',
            title: 'Quieres publicar un viaje redondo?',
            text: 'Si aceptas, repetimos el flujo para crear viaje de regreso.',
            showCancelButton: true,
            confirmButtonText: 'Si, crear regreso',
            cancelButtonText: 'No, finalizar',
            confirmButtonColor: '#0d6efd',
          });
          if (confirmRoundTrip.isConfirmed) {
            this.prepareReturnTrip();
            return;
          }
        }

        await this.suggestProfilePhotoIfNeeded();
        this.$router.replace('/trips/consulta');
      } catch (e) {
        await Swal.fire({
          icon: 'error',
          title: 'No se pudo publicar',
          text: normalizeApiError(e, 'Error al publicar viaje.'),
          confirmButtonColor: '#0d6efd',
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.blabla-trip-flow {
  background: #f4f6fb;
  min-height: calc(100vh - 100px);
  padding-bottom: 1.25rem;
}
.flow-card {
  border-radius: 18px;
  border: 1px solid #dbe4f2;
  box-shadow: 0 8px 24px rgba(14, 42, 90, 0.08);
}
.flow-title {
  text-align: center;
  color: #08214a;
  font-size: clamp(1.6rem, 2.6vw, 2.4rem);
  font-weight: 800;
  margin-bottom: 1rem;
}
.flow-section {
  max-width: 980px;
  margin: 0 auto;
}
.date-time-stack {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 520px;
  margin: 0 auto;
  align-items: center;
}
.date-block,
.time-block {
  width: 100%;
  max-width: 520px;
}
.route-map {
  height: 320px;
  border: 1px solid #d6dcea;
  border-radius: 12px;
}
.route-list .list-group-item {
  border-radius: 12px;
  margin-bottom: 10px;
  border: 1px solid #d6dcea;
}
.route-list .list-group-item.active {
  background: #0d6efd;
  border-color: #0d6efd;
}
.bbc-input {
  border-radius: 28px;
  border: 2px solid #0d6efd;
  min-height: 48px;
}
.flow-actions .btn {
  min-width: 130px;
  border-radius: 24px;
}
</style>

